/**
 * Sountsou 2026 - Grille d'articles (index.php)
 * Filtrage par catégorie, recherche et pagination, entièrement côté client.
 */

const PER_PAGE = 9;
const SEARCH_DEBOUNCE_MS = 280;

function normalize(str) {
    return (str || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
}

function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// Construit la liste des pages à afficher avec troncature : 1 … n-1 n n+1 … dernière
function buildPageList(current, total) {
    const pages = [];
    const add = (p) => {
        if (!pages.includes(p)) {
            pages.push(p);
        }
    };

    add(1);
    for (let p = current - 1; p <= current + 1; p++) {
        if (p > 1 && p < total) {
            add(p);
        }
    }
    if (total > 1) {
        add(total);
    }

    pages.sort((a, b) => a - b);

    const withEllipses = [];
    let previous = null;
    pages.forEach((p) => {
        if (previous !== null && p - previous > 1) {
            withEllipses.push('…');
        }
        withEllipses.push(p);
        previous = p;
    });

    return withEllipses;
}

export function initBlogCardsFilter(root = document) {
    const grids = root.querySelectorAll('.sountsou-blog-grid');

    grids.forEach((gridEl) => {
        if (gridEl.dataset.blogGridInitialized === 'true') {
            return;
        }
        gridEl.dataset.blogGridInitialized = 'true';

        const listEl = gridEl.querySelector('.sountsou-blog-grid__list');
        if (!listEl) {
            // Rien à filtrer (page vide : aucun article publié).
            return;
        }

        const itemEls = Array.from(listEl.querySelectorAll('.sountsou-blog-grid__item'));
        const filterBtns = Array.from(gridEl.querySelectorAll('.sountsou-blog-grid__filter-btn'));
        const searchForm = gridEl.querySelector('[data-blog-search-form]');
        const searchInput = gridEl.querySelector('.sountsou-blog-grid__search-input');
        const searchClear = gridEl.querySelector('[data-blog-search-clear]');
        const countEl = gridEl.querySelector('[data-blog-count]');
        const statusEl = gridEl.querySelector('.sountsou-blog-grid__status');
        const emptyEl = gridEl.querySelector('[data-blog-empty]');
        const paginationNav = gridEl.querySelector('[data-blog-pagination]');
        const paginationList = gridEl.querySelector('[data-blog-pagination-list]');

        const items = itemEls.map((el) => ({
            el,
            categories: (el.dataset.categories || '').split(/\s+/).filter(Boolean),
            title: normalize(el.dataset.title),
        }));

        const state = { category: 'all', search: '', page: 1 };
        let debounceTimer = null;

        function getFiltered() {
            return items.filter((item) => {
                const matchesCategory = state.category === 'all' || item.categories.includes(state.category);
                const matchesSearch = state.search === '' || item.title.includes(state.search);
                return matchesCategory && matchesSearch;
            });
        }

        function updateStatus(total) {
            const hasFilter = state.category !== 'all' || state.search !== '';
            const text = hasFilter
                ? (total === 0 ? 'Aucun article trouvé' : `${total} article${total > 1 ? 's' : ''} trouvé${total > 1 ? 's' : ''}`)
                : `${total} article${total > 1 ? 's' : ''}`;

            if (statusEl) {
                statusEl.textContent = text + '.';
            }
            if (countEl) {
                countEl.textContent = text;
            }
        }

        function renderPagination(totalPages) {
            if (!paginationNav || !paginationList) {
                return;
            }

            if (totalPages <= 1) {
                paginationNav.hidden = true;
                paginationList.innerHTML = '';
                return;
            }
            paginationNav.hidden = false;

            const pages = buildPageList(state.page, totalPages);
            paginationList.innerHTML = pages.map((p) => {
                if (p === '…') {
                    return '<li class="sountsou-blog-grid__pagination-ellipsis" aria-hidden="true">…</li>';
                }
                const isCurrent = p === state.page;
                return `<li>
                    <button type="button" class="sountsou-blog-grid__pagination-btn" data-page="${p}"${isCurrent ? ' aria-current="page"' : ''}>
                        <span class="sountsou-blog-grid__visually-hidden">Page </span>${p}
                    </button>
                </li>`;
            }).join('');
        }

        function render() {
            const filtered = getFiltered();
            const total = filtered.length;
            const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

            if (state.page > totalPages) {
                state.page = totalPages;
            }
            if (state.page < 1) {
                state.page = 1;
            }

            const start = (state.page - 1) * PER_PAGE;
            const visible = new Set(filtered.slice(start, start + PER_PAGE).map((item) => item.el));

            items.forEach((item) => {
                item.el.hidden = !visible.has(item.el);
            });

            if (emptyEl) {
                emptyEl.hidden = total !== 0;
            }

            renderPagination(totalPages);
            updateStatus(total);
        }

        filterBtns.forEach((btn) => {
            btn.addEventListener('click', () => {
                state.category = btn.dataset.categorySlug;
                state.page = 1;
                filterBtns.forEach((b) => {
                    const active = b === btn;
                    b.classList.toggle('is-active', active);
                    b.setAttribute('aria-pressed', String(active));
                });
                render();
            });
        });

        if (searchInput) {
            if (searchClear) {
                searchClear.hidden = !searchInput.value;
            }

            searchInput.addEventListener('input', () => {
                if (searchClear) {
                    searchClear.hidden = !searchInput.value;
                }
                clearTimeout(debounceTimer);
                debounceTimer = setTimeout(() => {
                    state.search = normalize(searchInput.value.trim());
                    state.page = 1;
                    render();
                }, SEARCH_DEBOUNCE_MS);
            });
        }

        if (searchClear && searchInput) {
            searchClear.addEventListener('click', () => {
                searchInput.value = '';
                searchClear.hidden = true;
                clearTimeout(debounceTimer);
                state.search = '';
                state.page = 1;
                render();
                searchInput.focus();
            });
        }

        if (searchForm) {
            searchForm.addEventListener('submit', (e) => {
                e.preventDefault();
                clearTimeout(debounceTimer);
                state.search = normalize(searchInput ? searchInput.value.trim() : '');
                state.page = 1;
                render();
            });
        }

        if (paginationList) {
            paginationList.addEventListener('click', (e) => {
                const btn = e.target.closest('[data-page]');
                if (!btn) {
                    return;
                }
                state.page = parseInt(btn.dataset.page, 10) || 1;
                render();
                gridEl.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
            });
        }

        render();
    });
}
